<template>
    <EDCHeader />
    <EDCHead title="Acerca de Enamórate del Cesar" />
    <div class="about-page">
        <header class="about-hero">
            <div class="about-hero__media">
                <img
                    src="https://almacenamientounicesar.blob.core.windows.net/enamoratedelcesar/assets/media/media/business/image_item/Banner_4.jpg"
                    alt="Paisaje y territorio del departamento del Cesar"
                    class="about-hero__img"
                    width="1600"
                    height="700"
                />
                <div class="about-hero__overlay" aria-hidden="true" />
            </div>
            <div class="about-hero__headline">
                <p class="about-hero__eyebrow">Territorio, cultura y tecnología</p>
                <h1 class="about-hero__title">Enamórate del Cesar</h1>
                <p class="about-hero__subtitle">
                    Un proyecto para descubrir el departamento, sus municipios y la memoria que los habita.
                </p>
            </div>
        </header>

        <div class="about__content">
            <section class="about-section about-section--lead" aria-labelledby="about-mission">
                <h2 id="about-mission" class="about-section__title">Qué es este proyecto</h2>
                <p class="about-lead">
                    Enamórate del Cesar es una apuesta por la cultura y el patrimonio del departamento: recuperar
                    tradiciones que siguen vivas, honrar raíces que nos definen y volver a mirar con orgullo lo que,
                    con el tiempo, a veces se vuelve invisible.
                </p>
                <p class="about-text">
                    Queremos que más personas se apropien de las manifestaciones que nos son propias y reconozcan la
                    riqueza paisajística y humana de nuestra tierra —desde el valle hasta la serranía, en cada
                    municipio con historia propia.
                </p>
            </section>

            <section class="about-section" aria-labelledby="about-tech">
                <h2 id="about-tech" class="about-section__title">Cómo lo vivimos contigo</h2>
                <p class="about-text">
                    Nos apoyamos en la tecnología para acercar personas y territorios a pesar de las distancias, y para
                    llevar las visitas a sitios de interés a otro nivel. En la web y en la app reunimos contenidos que
                    invitan a explorar el Cesar con otra mirada.
                </p>
                <ul class="about-highlights">
                    <li class="about-highlights__item">
                        <span class="about-highlights__label">Web</span>
                        <span class="about-highlights__text">Información por municipio, rutas de interés y experiencias interactivas en el navegador.</span>
                    </li>
                    <li class="about-highlights__item">
                        <span class="about-highlights__label">Video</span>
                        <span class="about-highlights__text">Contenido en alta definición y recorridos en 360° para sentir el paisaje antes de llegar.</span>
                    </li>
                    <li class="about-highlights__item">
                        <span class="about-highlights__label">App</span>
                        <span class="about-highlights__text">Experiencias en realidad aumentada que acercan el territorio a tu entorno cotidiano.</span>
                    </li>
                </ul>
            </section>

            <section class="about-section about-card" aria-labelledby="about-who">
                <h2 id="about-who" class="about-section__title about-card__title">Quiénes somos</h2>
                <ul class="about-partners" role="list" aria-label="Instituciones y empresas aliadas">
                    <li class="about-partners__item">
                        <img
                            src="../assets/logos/logo-upc.png"
                            alt="Universidad Popular del Cesar"
                            class="about-partners__logo"
                            width="200"
                            height="80"
                            loading="lazy"
                        />
                    </li>
                    <li class="about-partners__item">
                        <img
                            src="../assets/logos/logo-blazing.png"
                            alt="Blazing Soft"
                            class="about-partners__logo"
                            width="200"
                            height="80"
                            loading="lazy"
                        />
                    </li>
                    <li class="about-partners__item">
                        <img
                            src="../assets/logos/logo-certika.png"
                            alt="Certika"
                            class="about-partners__logo"
                            width="200"
                            height="80"
                            loading="lazy"
                        />
                    </li>
                </ul>
                <p class="about-text about-card__text">
                    Este proyecto es resultado de una alianza entre la <strong>Universidad Popular del Cesar</strong> y
                    las empresas de tecnología <strong>Blazing Soft</strong> y <strong>Certika</strong>, que acompañan la
                    investigación, el diseño y el desarrollo de la plataforma.
                </p>
            </section>

            <section class="about-section about-section--closing" aria-labelledby="about-reach">
                <h2 id="about-reach" class="about-section__title">Nuestro alcance</h2>
                <p class="about-text">
                    Hemos recorrido los municipios del departamento documentando paisajes y cultura local. El proyecto
                    también ha llegado a distintas partes del mundo para dar a conocer la riqueza del Cesar y cómo la
                    experiencia de los usuarios se amplifica cuando la tecnología sirve de puente hacia el territorio.
                </p>
            </section>
        </div>
    </div>
    <EDCFooter />
    <EDCMobileNav />
</template>

<script>
import EDCHeader from '../components/Header.vue';
import EDCHead from '../components/Head.vue';
import EDCFooter from '../components/Footer.vue';
import EDCMobileNav from '../components/MobileNav.vue';
import { Spinner } from '@/scripts/open-spinner';

export default {
    name: "edc-about-view",
    components: {
        EDCHeader,
        EDCHead,
        EDCFooter,
        EDCMobileNav
    },
    mounted() {
        Spinner.show();
        this.$nextTick(() => {
            setTimeout(() => Spinner.hide(), 450);
        });
    }
}
</script>

<style>
.about-page {
    width: 100%;
    margin-bottom: 48px;
}

.about-hero {
    position: relative;
    width: 100%;
    margin-bottom: 0;
}

.about-hero__media {
    position: relative;
    width: 100%;
    max-height: min(52vh, 420px);
    overflow: hidden;
}

.about-hero__img {
    width: 100%;
    height: min(52vh, 420px);
    object-fit: cover;
    display: block;
}

.about-hero__overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
        180deg,
        rgba(20, 18, 24, 0.25) 0%,
        rgba(20, 18, 24, 0.65) 55%,
        rgba(20, 18, 24, 0.88) 100%
    );
    pointer-events: none;
}

.about-hero__headline {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 28px 20px 36px;
    max-width: 1000px;
    margin: 0 auto;
    box-sizing: border-box;
}

.about-hero__eyebrow {
    margin: 0 0 8px;
    font-size: 0.8125rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.85);
}

.about-hero__title {
    margin: 0 0 10px;
    font-size: clamp(1.75rem, 4vw, 2.35rem);
    font-weight: 800;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: #fff;
    text-shadow: 0 2px 24px rgba(0, 0, 0, 0.35);
}

.about-hero__subtitle {
    margin: 0;
    max-width: 36em;
    font-size: clamp(0.95rem, 2vw, 1.0625rem);
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.92);
}

.about__content {
    width: 100%;
    max-width: 720px;
    padding: 32px 20px 0;
    margin: 0 auto;
    box-sizing: border-box;
}

.about-section {
    margin-bottom: 36px;
}

.about-section--lead {
    padding-top: 8px;
}

.about-section__title {
    margin: 0 0 14px;
    font-size: 1.25rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--color-primary, #a153aa);
    line-height: 1.25;
}

.about-lead {
    margin: 0 0 16px;
    font-size: 1.0625rem;
    line-height: 1.65;
    color: var(--color-dark, #222);
    font-weight: 500;
    text-align: justify;
    hyphens: auto;
}

.about-text {
    margin: 0 0 14px;
    font-size: 1rem;
    line-height: 1.7;
    color: #3a3a3a;
    text-align: justify;
    hyphens: auto;
}

.about-text:last-child {
    margin-bottom: 0;
}

.about-highlights {
    list-style: none;
    margin: 20px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.about-highlights__item {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 12px 16px;
    align-items: start;
    padding: 16px 18px;
    border-radius: 14px;
    border: 1px solid rgba(161, 83, 170, 0.18);
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 246, 252, 0.95) 100%);
    box-shadow: 0 2px 14px rgba(161, 83, 170, 0.06);
}

.about-highlights__label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 3.25rem;
    padding: 6px 10px;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #fff;
    background: var(--color-primary, #a153aa);
    border-radius: 8px;
    line-height: 1.2;
}

.about-highlights__text {
    margin: 0;
    font-size: 0.9375rem;
    line-height: 1.55;
    color: #444;
    text-align: justify;
    hyphens: auto;
}

.about-card {
    padding: 22px 22px 24px;
    border-radius: 16px;
    border: 1px solid rgba(177, 205, 74, 0.35);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 252, 238, 0.5) 100%);
    box-shadow: 0 4px 20px rgba(177, 205, 74, 0.08);
}

.about-card__title {
    margin-bottom: 12px;
}

.about-card__text {
    margin-bottom: 0;
}

.about-partners {
    list-style: none;
    margin: 0 0 22px;
    padding: 16px 12px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 20px 28px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.65);
    border: 1px solid rgba(161, 83, 170, 0.12);
}

.about-partners__item {
    margin: 0;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 52px;
}

.about-partners__logo {
    display: block;
    max-height: 52px;
    width: auto;
    max-width: min(160px, 32vw);
    object-fit: contain;
}

.about-section--closing {
    margin-bottom: 0;
    padding-bottom: 8px;
}

@media (min-width: 720px) {
    .about__content {
        padding-top: 40px;
        padding-left: 24px;
        padding-right: 24px;
    }

    .about-hero__headline {
        padding: 32px 24px 40px;
    }

    .about-section {
        margin-bottom: 44px;
    }
}
</style>
